import { Box, Button, Card, Checkbox, Group, ScrollArea, Stack, Text, UnstyledButton } from "@mantine/core";
import React, { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  selectIssueBoardIdsUserCanAccessFromUrl,
  selectMultiRegisterIssueBoardsPanelRows,
  selectMultiRegisterIssuesActiveIssueBoardIds,
} from "./multiRegisterIssuesSelectors";
import { setMultiRegisterIssuesIssueBoardIdsFilterInRedux } from "./multiRegisterIssuesSlice";

const MultiRegisterIssueBoardsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const allIssueBoardIds = useAppSelector(selectIssueBoardIdsUserCanAccessFromUrl);
  const activeIssueBoardIds = useAppSelector(selectMultiRegisterIssuesActiveIssueBoardIds);
  const issueBoardRows = useAppSelector(selectMultiRegisterIssueBoardsPanelRows);
  const activeIssueBoardIdsSet = React.useMemo(() => new Set(activeIssueBoardIds), [activeIssueBoardIds]);
  const allSelected = allIssueBoardIds.length > 0 && activeIssueBoardIds.length === allIssueBoardIds.length;

  const setAllSelected = useCallback(() => {
    dispatch(setMultiRegisterIssuesIssueBoardIdsFilterInRedux(null));
  }, [dispatch]);

  const setNoneSelected = useCallback(() => {
    dispatch(setMultiRegisterIssuesIssueBoardIdsFilterInRedux([]));
  }, [dispatch]);

  const toggleIssueBoard = useCallback(
    (issueBoardId: string) => {
      const nextActiveIssueBoardIds = activeIssueBoardIdsSet.has(issueBoardId)
        ? activeIssueBoardIds.filter((activeIssueBoardId) => activeIssueBoardId !== issueBoardId)
        : [...activeIssueBoardIds, issueBoardId];

      dispatch(
        setMultiRegisterIssuesIssueBoardIdsFilterInRedux(
          nextActiveIssueBoardIds.length === allIssueBoardIds.length ? null : nextActiveIssueBoardIds,
        ),
      );
    },
    [activeIssueBoardIds, activeIssueBoardIdsSet, allIssueBoardIds.length, dispatch],
  );

  return (
    <Card withBorder radius="sm" p="sm" bg="#fafafb" bd="1px solid var(--mantine-color-warmgray-2)" h="100%">
      <Stack gap="sm" h="100%">
        <Box>
          <Text fw={600} size="sm">
            Registre
          </Text>
          <Text size="xs" c="dimmed">
            {activeIssueBoardIds.length} av {allIssueBoardIds.length} valgt
          </Text>
        </Box>
        <Group gap="xs" wrap="nowrap">
          <Button size="xs" variant={allSelected ? "filled" : "light"} color="mc-primary" onClick={setAllSelected}>
            Alle
          </Button>
          <Button size="xs" variant="subtle" color="warmgray.7" onClick={setNoneSelected}>
            Ingen
          </Button>
        </Group>
        <ScrollArea.Autosize mah="calc(70vh + 92px)" offsetScrollbars>
          <Stack gap="xs">
            {issueBoardRows.map(({ issueBoard, count }) => {
              return (
                <UnstyledButton key={issueBoard._id} w="100%" onClick={() => toggleIssueBoard(issueBoard._id)}>
                  <Box
                    display="grid"
                    style={{
                      gridTemplateColumns: "24px minmax(0, 1fr) 48px",
                      alignItems: "center",
                      columnGap: 8,
                    }}
                    w="100%"
                  >
                    <Checkbox
                      checked={activeIssueBoardIdsSet.has(issueBoard._id)}
                      size="sm"
                      onChange={() => undefined}
                      style={{ pointerEvents: "none" }}
                    />
                    <Text size="xs" truncate="end" style={{ minWidth: 0 }}>
                      {issueBoard.name}
                    </Text>
                    <Box
                      px={6}
                      py={1}
                      style={{
                        border: `1px solid ${
                          count === 0 ? "var(--mantine-color-warmgray-3)" : "var(--mantine-color-warmgray-5)"
                        }`,
                        borderRadius: 6,
                        backgroundColor: count === 0 ? "transparent" : "var(--mantine-color-warmgray-0)",
                        color: count === 0 ? "var(--mantine-color-warmgray-5)" : "var(--mantine-color-warmgray-8)",
                        fontSize: 12,
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1.35,
                        textAlign: "right",
                        width: 42,
                      }}
                    >
                      {count}
                    </Box>
                  </Box>
                </UnstyledButton>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </Card>
  );
};

export default MultiRegisterIssueBoardsPanel;
